import React from 'react';
import ConnectAccounts from '../ConnectAccounts';

/**
 * Live Auto-Apply tab content. Browser-extension based — runs LinkedIn,
 * Indeed, Dice, Monster inside the user's logged-in browser session.
 *
 * Renders:
 *   - Section header
 *   - Live console (streaming logs from the extension)
 *   - "Applications This Session" cards grid
 *   - ConnectAccounts widget (when extension isn't installed)
 *   - Extension install banner
 *
 * Pure presentation. Parent owns all state; we receive everything as props.
 */
export default function LiveTab({
  // Console
  logs,
  isRunning,
  consoleBodyRef,
  consoleEndRef,

  // Applied jobs
  appliedJobs,

  // Extension presence + connect-accounts
  extInstalled,
  user,
  preferences,
  setCanStart,
  apiBaseUrl,
}) {
  return (
    <div key="tab-live" className="ajp-tab-pane">

      {/* Section header */}
      <div className="ajp-section-header">
        <div className="ajp-section-header-icon">⚡</div>
        <div>
          <div className="ajp-section-header-title">Live Auto-Apply</div>
          <div className="ajp-section-header-sub">
            Watch jobs apply in your browser. Requires this tab open.
          </div>
        </div>
      </div>

      {/* Console */}
      <div className="ajp-console glass">
        <div className="ajp-console-header">
          <span>🖥️ Live Application Console</span>
        </div>
        <div className="ajp-console-body" ref={consoleBodyRef}>
          {logs.length === 0 && !isRunning && (
            <div className="ajp-empty" style={{ padding: '32px 16px' }}>
              <div className="ajp-empty-icon">⚡</div>
              <div className="ajp-empty-title">Ready to apply.</div>
              <div className="ajp-empty-sub">
                Pick your platforms above, then click <strong>Launch Auto Apply</strong>. We'll stream every step right here as it happens.
              </div>
            </div>
          )}
          {logs.map((log, i) => (
            <div key={i} className={`ajp-log ajp-log-${log.type}`}>
              <span className="ajp-log-time">[{log.time}]</span><span>{log.message}</span>
            </div>
          ))}
          <div ref={consoleEndRef} />
        </div>
      </div>

      {/* Applications This Session */}
      {appliedJobs.length > 0 && (
        <div className="ajp-jobs-cards">
          <div className="ajp-jobs-cards-header">
            <h4 className="ajp-jobs-cards-title">Applications This Session</h4>
            <span className="ajp-jobs-count">{appliedJobs.length} jobs</span>
          </div>
          <div className="ajp-cards-grid">
            {[...appliedJobs].reverse().map((job, i) => {
              const statusConfig = {
                applied:  { label: 'Applied',      icon: '✅', cls: 'applied'  },
                skipped:  { label: 'Skipped',      icon: '⏭️', cls: 'skipped'  },
                applying: { label: 'Applying...',  icon: '⏳', cls: 'applying' },
                failed:   { label: 'Failed',       icon: '❌', cls: 'failed'   },
              };
              const s = statusConfig[job.status] || statusConfig.failed;
              const companyInitial = (job.company || job.platform || '?')[0].toUpperCase();
              const timeStr = job.appliedAt
                ? new Date(job.appliedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                : '';
              return (
                <div key={i} className={`ajp-job-card ajp-job-card--${s.cls}`}>
                  <div className="ajp-job-card-logo">
                    {job.logoUrl
                      ? <img src={job.logoUrl} alt={job.company} onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                      : null}
                    <span style={{ display: job.logoUrl ? 'none' : 'flex' }}>{companyInitial}</span>
                  </div>

                  <div className="ajp-job-card-body">
                    <div className="ajp-job-card-top">
                      <span className="ajp-job-card-title">{job.title || '—'}</span>
                      <span className="ajp-platform-tag">{job.platform}</span>
                    </div>
                    <div className="ajp-job-card-meta">
                      {job.company  && <span className="ajp-meta-item"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>{job.company}</span>}
                      {job.location && <span className="ajp-meta-item"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>{job.location}</span>}
                      {job.salary   && <span className="ajp-meta-item"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>{job.salary}</span>}
                    </div>
                    <div className="ajp-job-card-footer">
                      <div className="ajp-job-card-chips">
                        {job.employmentType && <span className="ajp-meta-chip">{job.employmentType}</span>}
                        {job.postedDate && <span className="ajp-meta-item ajp-meta-muted">Posted {job.postedDate}</span>}
                        {timeStr && <span className="ajp-meta-item ajp-meta-muted"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>{timeStr}</span>}
                      </div>
                      <span className={`ajp-job-status-badge ajp-job-status--${s.cls}`}>{s.icon} {s.label}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Connect-accounts widget when no extension */}
      {!extInstalled && !isRunning && (
        <ConnectAccounts
          token={localStorage.getItem('token')}
          userId={user?.id}
          platforms={preferences.platforms}
          onReady={() => setCanStart(true)}
          apiBaseUrl={apiBaseUrl}
        />
      )}

      {!extInstalled && (
        <div className="ajp-ext-banner ajp-ext-banner--install">
          <span>🔌 Install the <strong>Terview Chrome Extension</strong> for undetectable automation — no bot blocks, no Puppeteer</span>
        </div>
      )}
    </div>
  );
}